import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import { ChatBubbleLeftRightIcon, StarIcon } from '@heroicons/react/24/outline';
import { getConversationFlags, getReviewFlags, resolveConversationFlag, resolveReviewFlag } from '@/services/api';
import { Card, CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Modal, { ModalFooter } from '@/components/common/Modal';
import Select from '@/components/common/Select';
import { PriorityBadge } from '@/components/common/Badge';
import EmptyState from '@/components/common/EmptyState';
import { formatTimeAgo } from '@/utils/formatters';
import toast from 'react-hot-toast';
import type { ConversationFlag, ReviewFlag } from '@/types/moderation';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function Moderation() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const initialTab = searchParams.get('tab') === 'reviews' ? 1 : 0;
  const [selectedTab, setSelectedTab] = useState(initialTab);

  // Conversation flags state
  const [selectedConvFlag, setSelectedConvFlag] = useState<ConversationFlag | null>(null);
  const [convResolution, setConvResolution] = useState('violation_confirmed');
  const [convAction, setConvAction] = useState('warning_issued');
  const [convNotes, setConvNotes] = useState('');

  // Review flags state
  const [selectedReviewFlag, setSelectedReviewFlag] = useState<ReviewFlag | null>(null);
  const [reviewResolution, setReviewResolution] = useState('fake_confirmed');
  const [reviewAction, setReviewAction] = useState('review_removed');
  const [reviewNotes, setReviewNotes] = useState('');

  // Queries
  const { data: convFlags, isLoading: convLoading } = useQuery({
    queryKey: ['conversationFlags', 'pending'],
    queryFn: () => getConversationFlags('pending'),
  });

  const { data: reviewFlags, isLoading: reviewLoading } = useQuery({
    queryKey: ['reviewFlags', 'pending'],
    queryFn: () => getReviewFlags('pending'),
  });

  // Mutations
  const resolveConvMutation = useMutation({
    mutationFn: () => resolveConversationFlag(
      selectedConvFlag!.id,
      convResolution,
      convNotes,
      convAction,
      undefined,
      convAction === 'user_suspended' ? 7 : undefined
    ),
    onSuccess: () => {
      toast.success('Flag resolved successfully');
      queryClient.invalidateQueries({ queryKey: ['conversationFlags'] });
      queryClient.invalidateQueries({ queryKey: ['moderationCounts'] });
      setSelectedConvFlag(null);
      setConvNotes('');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to resolve flag');
    },
  });

  const resolveReviewMutation = useMutation({
    mutationFn: () => resolveReviewFlag(
      selectedReviewFlag!.id,
      reviewResolution,
      reviewNotes,
      reviewAction
    ),
    onSuccess: () => {
      toast.success('Review flag resolved successfully');
      queryClient.invalidateQueries({ queryKey: ['reviewFlags'] });
      queryClient.invalidateQueries({ queryKey: ['moderationCounts'] });
      setSelectedReviewFlag(null);
      setReviewNotes('');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to resolve flag');
    },
  });

  const tabs = [
    { name: 'Conversation Flags', icon: ChatBubbleLeftRightIcon, count: convFlags?.total || 0 },
    { name: 'Review Flags', icon: StarIcon, count: reviewFlags?.total || 0 },
  ];

  const convResolutionOptions = [
    { value: 'violation_confirmed', label: 'Violation Confirmed' },
    { value: 'no_violation', label: 'No Violation' },
    { value: 'dismissed', label: 'Dismissed' },
  ];

  const convActionOptions = [
    { value: 'warning_issued', label: 'Issue Warning' },
    { value: 'user_suspended', label: 'Suspend User (7 days)' },
    { value: 'user_banned', label: 'Ban User' },
    { value: 'no_action', label: 'No Action' },
  ];

  const reviewResolutionOptions = [
    { value: 'fake_confirmed', label: 'Fake Review Confirmed' },
    { value: 'legitimate_review', label: 'Legitimate Review' },
    { value: 'dismissed', label: 'Dismissed' },
  ];

  const reviewActionOptions = [
    { value: 'review_removed', label: 'Remove Review' },
    { value: 'review_kept', label: 'Keep Review' },
    { value: 'reviewer_warned', label: 'Warn Reviewer' },
    { value: 'reviewer_banned', label: 'Ban Reviewer' },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Content Moderation</h1>
        <p className="mt-1 text-sm text-gray-500">
          Review and resolve flagged content and ToS violations
        </p>
      </div>

      {/* Tabs */}
      <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
        <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1">
          {tabs.map((tab) => (
            <Tab
              key={tab.name}
              className={({ selected }) =>
                classNames(
                  'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                  'ring-white ring-opacity-60 ring-offset-2 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-white text-brand shadow'
                    : 'text-gray-600 hover:bg-white/50 hover:text-gray-900'
                )
              }
            >
              <div className="flex items-center justify-center gap-2">
                <tab.icon className="w-5 h-5" />
                {tab.name}
                {tab.count > 0 && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                    {tab.count}
                  </span>
                )}
              </div>
            </Tab>
          ))}
        </Tab.List>

        <Tab.Panels className="mt-4">
          {/* Conversation Flags Panel */}
          <Tab.Panel>
            <Card>
              <CardBody>
                {convLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : convFlags?.flags && convFlags.flags.length > 0 ? (
                  <div className="space-y-4">
                    {convFlags.flags.map((flag) => (
                      <button
                        type="button"
                        key={flag.id}
                        className="w-full p-4 border border-gray-200 rounded-lg hover:border-brand transition-colors cursor-pointer text-left"
                        onClick={() => setSelectedConvFlag(flag)}
                        aria-label={`Open conversation flag ${flag.flag_type.replace('_', ' ')}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900 capitalize">
                                {flag.flag_type.replace('_', ' ')}
                              </span>
                              <PriorityBadge priority={flag.priority} />
                            </div>
                            <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                              {flag.description}
                            </p>
                            <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                              <span>Reported by: {flag.reporter?.full_name || flag.reporter?.email}</span>
                              <span>Against: {flag.flagged_user?.full_name || flag.flagged_user?.email}</span>
                            </div>
                          </div>
                          <span className="text-sm text-gray-500">{formatTimeAgo(flag.created_at)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={<ChatBubbleLeftRightIcon className="w-12 h-12" />}
                    title="No pending conversation flags"
                    description="All conversation reports have been reviewed"
                  />
                )}
              </CardBody>
            </Card>
          </Tab.Panel>

          {/* Review Flags Panel */}
          <Tab.Panel>
            <Card>
              <CardBody>
                {reviewLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : reviewFlags?.flags && reviewFlags.flags.length > 0 ? (
                  <div className="space-y-4">
                    {reviewFlags.flags.map((flag) => (
                      <button
                        type="button"
                        key={flag.id}
                        className="w-full p-4 border border-gray-200 rounded-lg hover:border-brand transition-colors cursor-pointer text-left"
                        onClick={() => setSelectedReviewFlag(flag)}
                        aria-label={`Open review flag ${flag.flag_reason.replace('_', ' ')}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900 capitalize">
                                {flag.flag_reason.replace('_', ' ')}
                              </span>
                              <PriorityBadge priority={flag.priority} />
                            </div>
                            <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                              {flag.evidence_description}
                            </p>
                            <div className="mt-2 text-xs text-gray-400">
                              Business: {flag.business?.name || 'Unknown'}
                            </div>
                          </div>
                          <span className="text-sm text-gray-500">{formatTimeAgo(flag.created_at)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={<StarIcon className="w-12 h-12" />}
                    title="No pending review flags"
                    description="All review reports have been reviewed"
                  />
                )}
              </CardBody>
            </Card>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      {/* Resolve Conversation Flag Modal */}
      <Modal
        isOpen={!!selectedConvFlag}
        onClose={() => setSelectedConvFlag(null)}
        title="Resolve Conversation Flag"
        size="lg"
      >
        {selectedConvFlag && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-medium capitalize">{selectedConvFlag.flag_type.replace('_', ' ')}</span>
                <PriorityBadge priority={selectedConvFlag.priority} />
              </div>
              <p className="text-sm text-gray-600">{selectedConvFlag.description}</p>
              <div className="mt-2 text-xs text-gray-400">
                <p>Reported: {formatTimeAgo(selectedConvFlag.created_at)}</p>
                <p>Against: {selectedConvFlag.flagged_user?.full_name || selectedConvFlag.flagged_user?.email}</p>
              </div>
            </div>

            <Select
              label="Resolution"
              value={convResolution}
              onChange={setConvResolution}
              options={convResolutionOptions}
            />

            {convResolution === 'violation_confirmed' && (
              <Select
                label="Action"
                value={convAction}
                onChange={setConvAction}
                options={convActionOptions}
              />
            )}

            <div>
              <label htmlFor="conversation-resolution-notes" className="block text-sm font-medium text-gray-700 mb-1">
                Resolution Notes
              </label>
              <textarea
                id="conversation-resolution-notes"
                name="conversation_resolution_notes"
                value={convNotes}
                onChange={(e) => setConvNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                rows={3}
                placeholder="Enter resolution notes..."
                aria-label="Conversation resolution notes"
              />
            </div>
          </div>
        )}
        <ModalFooter>
          <Button variant="secondary" onClick={() => setSelectedConvFlag(null)}>
            Cancel
          </Button>
          <Button
            onClick={() => resolveConvMutation.mutate()}
            loading={resolveConvMutation.isPending}
            disabled={!convNotes.trim()}
          >
            Resolve Flag
          </Button>
        </ModalFooter>
      </Modal>

      {/* Resolve Review Flag Modal */}
      <Modal
        isOpen={!!selectedReviewFlag}
        onClose={() => setSelectedReviewFlag(null)}
        title="Resolve Review Flag"
        size="lg"
      >
        {selectedReviewFlag && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-medium capitalize">{selectedReviewFlag.flag_reason.replace('_', ' ')}</span>
                <PriorityBadge priority={selectedReviewFlag.priority} />
              </div>
              <p className="text-sm text-gray-600">{selectedReviewFlag.evidence_description}</p>
              <div className="mt-2 text-xs text-gray-400">
                <p>Business: {selectedReviewFlag.business?.name}</p>
                <p>Reported: {formatTimeAgo(selectedReviewFlag.created_at)}</p>
              </div>
            </div>

            <Select
              label="Resolution"
              value={reviewResolution}
              onChange={setReviewResolution}
              options={reviewResolutionOptions}
            />

            <Select
              label="Action"
              value={reviewAction}
              onChange={setReviewAction}
              options={reviewActionOptions}
            />

            <div>
              <label htmlFor="review-resolution-notes" className="block text-sm font-medium text-gray-700 mb-1">
                Resolution Notes
              </label>
              <textarea
                id="review-resolution-notes"
                name="review_resolution_notes"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                rows={3}
                placeholder="Enter resolution notes..."
                aria-label="Review resolution notes"
              />
            </div>
          </div>
        )}
        <ModalFooter>
          <Button variant="secondary" onClick={() => setSelectedReviewFlag(null)}>
            Cancel
          </Button>
          <Button
            onClick={() => resolveReviewMutation.mutate()}
            loading={resolveReviewMutation.isPending}
            disabled={!reviewNotes.trim()}
          >
            Resolve Flag
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
